import { jwtDecode } from 'jwt-decode';

/**
 * Session utility functions for managing user authentication state
 */

/**
 * Check if the current session token is valid and not expired
 * @param {string} token - JWT token to validate
 * @returns {boolean} - True if token is valid and not expired
 */
export const isSessionValid = (token) => {
    if (!token) {
        return false;
    }

    try {
        const decodedToken = jwtDecode(token);
        const currentTimestamp = Date.now() / 1000;
        
        // Check if token has expired
        if (decodedToken.exp < currentTimestamp) {
            console.log('Token has expired');
            return false;
        }
        
        return true;
    } catch (error) {
        console.error('Error validating token:', error);
        return false;
    }
};

/**
 * Get remaining session time in seconds
 * @param {string} token - JWT token
 * @returns {number} - Remaining time in seconds, or 0 if expired/invalid
 */
export const getRemainingSessionTime = (token) => {
    if (!token) {
        return 0;
    }

    try {
        const decodedToken = jwtDecode(token);
        const currentTimestamp = Date.now() / 1000;
        const remainingTime = decodedToken.exp - currentTimestamp;
        
        return Math.max(0, Math.floor(remainingTime));
    } catch (error) {
        console.error('Error calculating remaining session time:', error);
        return 0;
    }
};

/**
 * Check if session is about to expire (within warning threshold)
 * @param {string} token - JWT token
 * @param {number} warningThreshold - Warning threshold in seconds (default: 300 = 5 minutes)
 * @returns {boolean} - True if session is about to expire
 */
export const isSessionAboutToExpire = (token, warningThreshold = 300) => {
    const remainingTime = getRemainingSessionTime(token);
    return remainingTime > 0 && remainingTime <= warningThreshold;
};

/**
 * Get session expiry date
 * @param {string} token - JWT token
 * @returns {Date|null} - Expiry date or null if invalid token
 */
export const getSessionExpiryDate = (token) => {
    if (!token) {
        return null;
    }

    try {
        const decodedToken = jwtDecode(token);
        return new Date(decodedToken.exp * 1000);
    } catch (error) {
        console.error('Error getting session expiry date:', error);
        return null;
    }
};

/**
 * Format remaining time as human readable string
 * @param {number} seconds - Remaining seconds
 * @returns {string} - Formatted time string
 */
export const formatRemainingTime = (seconds) => {
    if (seconds <= 0) {
        return 'Expired';
    }

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
        return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
        return `${minutes}m ${secs}s`;
    } else {
        return `${secs}s`;
    }
};

/**
 * Session validation hook for React components
 * Use this in components that need to check session validity on mount
 */
export const useSessionValidation = (token, logout) => {
    const validateSession = () => {
        if (!isSessionValid(token)) {
            console.log('Invalid session detected, logging out...');
            logout();
            return false;
        }
        return true;
    };

    return { validateSession };
};